import { getEnv } from 'config/env';
import { Env } from 'types';
import { logError } from './logger';
import assetFixture from 'assets/fixtures/asset.json';
import courseFixture from 'assets/fixtures/course.json';

const fixtures: Record<string, unknown[]> = {
  '/asset': assetFixture as unknown[],
  '/course': courseFixture as unknown[],
};

const tryFixture = (url: string): unknown | undefined => {
  if (fixtures[url]) return fixtures[url];
  const match = url.match(/^(\/asset|\/course)\/([^/?]+)$/);
  if (!match) return undefined;
  const [, base, id] = match;
  return (fixtures[base] as Array<Record<string, string>>).find(
    (item) => item.id === id || item.shortid === id,
  );
};

const urlMap: Record<string, string> = {
  [Env.DEV]: 'http://localhost:3000',
  [Env.PROD]: 'https://api.tonsbergseilforening.no',
  [Env.TEST]: '🤷‍♂️',
};

const getApiUrl = () => urlMap[getEnv()];

enum StatusCode {
  'InternalServerError' = 500,
  'BadRequest' = 400,
  'Forbidden' = 403,
  'NotFound' = 404,
  'Ok' = 200,
}

enum Method {
  POST = 'post',
  GET = 'get',
  PUT = 'put',
  DELETE = 'delete',
}

const headers = (token?: string | null) => ({
  'Content-Type': 'application/json',
  ...((token && { Authorization: token }) || {}),
});

export const handleApiError =
  (logger: (error: Error, message?: string) => void = logError) =>
    (error: Error | string, defaultMessage?: string) => {
      if (typeof error === 'string') {
        logger(new Error(error));
        return error;
      } else {
        logger(error, defaultMessage);
        return defaultMessage || error.message || 'unknown default message';
      }
    };

const errorHandler = handleApiError();

const request =
  (method: Method) =>
    async <T>(url: string, body?: any): Promise<T> => {
      if (getEnv() !== Env.PROD && method === Method.GET) {
        const fixture = tryFixture(url);
        if (fixture !== undefined) return fixture as T;
      }
      const token = window.localStorage.getItem('jwt');
      const response = await fetch(`${getApiUrl()}${url}`, {
        headers: headers(token),
        mode: 'cors',
        method,
        ...((body && { body: JSON.stringify(body) }) || {}),
      });
      const json = response.json();
      if (response.status === StatusCode.Ok) return json;
      const error = await json;
      throw error;
    };

export const post$ = request(Method.POST);

export const get$ = request(Method.GET);

export const put$ = request(Method.PUT);

export const delete$ = request(Method.DELETE);

export const silence = (statusCodes?: StatusCode[]) => (response: any) => {
  if (!statusCodes) return;
  if (statusCodes.includes(response.status)) return;
  else errorHandler(response);
};
